import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useNavigationType, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { undo, redo } from '../store/slices/coloringSlice';
import type { Image } from '../types';
import { FacebookShareButton, TwitterShareButton, LinkedinShareButton } from 'react-share';
import {useIsMobile} from "../hooks/useIsMobile";
import {Theme} from "../config/theme";

const truncateMiddle = (text: string, frontChars = 10, backChars = 2): string => {
  if (!text) {
    return '';
  }
  if (text.length <= frontChars + backChars + 3) return text;
  return text.slice(0, frontChars) + '...' + text.slice(-backChars);
};

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0;
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const IconButton = styled.button<{ disabled?: boolean }>`
  padding: 0.5rem;
  background: none;
  border: none;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => (props.disabled ? '#ccc' : '#666')};

  &:hover {
    color: ${props => (props.disabled ? '#ccc' : '#000')};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;

  ${IconButton} {
    padding: 0.75rem;
    min-width: 48px;
    min-height: 48px;
  }
`;

const Title = styled.h1`
  color: ${Theme.colors.main};
  font-size: 1.2rem;
  margin: 0;
  padding-right: 1rem;
`;

const HeaderRight = styled.div`
  display: flex;
  gap: 0.5rem;
  padding-right: 0.5rem;
`;

const ShareButton = styled(IconButton)`
  position: relative;
`;

const ShareMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: 1000;
`;

const ShareOption = styled.button`
  padding: 8px 12px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  color: ${Theme.colors.main};
  border-radius: 4px;

  &:hover {
    background: #f5f5f5;
  }
`;

interface ColoringHeaderProps {
  currentImage: Image;
  currentStep: number;
  historyLength: number;
  onShowToast: (message: string) => void;
  onResetRequest: () => void;
}

const ColoringHeader: React.FC<ColoringHeaderProps> = ({
                                                         currentImage,
                                                         currentStep,
                                                         historyLength,
                                                         onShowToast,
                                                         onResetRequest,
                                                       }) => {
  const navigate = useNavigate();
  const navType = useNavigationType();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const { categoryId } = useParams<{ categoryId: string }>();
  const [showShareMenu, setShowShareMenu] = useState(false);
  const shareButtonRef = useRef<HTMLButtonElement>(null);
  const url = `${window.location.origin}/coloring/${categoryId}/${currentImage.id}`;

  const shareTitle = `Check out this coloring I made! 🎨`;
  // const shareQuote = `Try coloring your own image at our coloring app! ✨`;
  const shareSummary = `Explore creative coloring online. This is what I made — you can try too!`;

  // Function to generate image data from the SVG
  const generateImageData = async () => {
    const svgContainer = document.querySelector('.svg-container');
    if (svgContainer) {
      const svgElement = svgContainer.querySelector('svg');
      if (svgElement) {
        const clonedSvg = svgElement.cloneNode(true) as SVGSVGElement;
        const style = document.createElementNS('http://www.w3.org/2000/svg', 'style');
        style.textContent = `rect, path { stroke: none !important; }`;
        clonedSvg.insertBefore(style, clonedSvg.firstChild);

        const svgData = new XMLSerializer().serializeToString(clonedSvg);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        return new Promise<string>((resolve) => {
          img.onload = () => {
            canvas.width = img.width;
            canvas.height = img.height;
            ctx?.drawImage(img, 0, 0);
            const dataUrl = canvas.toDataURL('image/png');
            resolve(dataUrl);
          };
          img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
        });
      }
    }
    return '';
  };

  const handleShare = async (type: 'link' | 'image' | 'native') => {
    if (type === 'link') {
      try {
        await navigator.clipboard.writeText(url);
        onShowToast('Link copied to clipboard!');
      } catch (error) {
        onShowToast('Failed to copy link');
      }
    } else if (type === 'image') {
      const imageData = await generateImageData();
      if (imageData) {
        const a = document.createElement('a');
        a.href = imageData;
        a.download = `${currentImage.name}-colored.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        onShowToast('Image downloaded!');
      }
    } else if (type === 'native') {
      const imageData = await generateImageData();
      if (navigator.share) {
        try {
          await navigator.share({
            title: currentImage.name,
            url: url,
            files: [new File([imageData], `${currentImage.name}-colored.png`, { type: 'image/png' })],
          });
          onShowToast('Shared successfully!');
        } catch (error) {
          onShowToast('Failed to share');
        }
      } else {
        try {
          await navigator.clipboard.writeText(url);
          onShowToast('Link copied to clipboard! You can share it manually.');
        } catch (error) {
          onShowToast('Failed to copy link');
        }
      }
    }

    setShowShareMenu(false);
  };

  const handleExit = () => {
    // State to pass to the Category page
    const navigationState = {
      from: 'coloring',
      imageId: currentImage.id
    };
    
    if (window.history.length > 1 && navType !== 'POP') {
      navigate(-1 as any, { state: navigationState });
    } else {
      navigate('/', { state: navigationState });
    }
  };

  return (
      <Header>
        <HeaderLeft>
          <IconButton onClick={handleExit} title="Back">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </IconButton>
          <Title>{truncateMiddle(currentImage.name)}</Title>
        </HeaderLeft>
        <HeaderRight>
          <IconButton
              onClick={() => {
                dispatch(undo());
              }}
              title="Undo"
              disabled={currentStep <= 0}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 10h10a8 8 0 018 8v2M3 10l6-6M3 10l6 6" />
            </svg>
          </IconButton>
          <IconButton
              onClick={() => {
                dispatch(redo());
              }}
              title="Redo"
              disabled={currentStep >= historyLength - 1}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 10H11a8 8 0 00-8 8v2M21 10l-6-6M21 10l-6 6" />
            </svg>
          </IconButton>
          <IconButton onClick={onResetRequest} title="Reset">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </IconButton>
          <ShareButton
              ref={shareButtonRef}
              onClick={() => setShowShareMenu(!showShareMenu)}
              title="Share"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
              <path d="M16 6l-4-4-4 4" />
              <path d="M12 2v12" />
            </svg>
            {showShareMenu && (
                <ShareMenu>
                  {!isMobile &&
                  (<><FacebookShareButton url={url} title={shareTitle}>
                    <ShareOption>Share on Facebook</ShareOption>
                  </FacebookShareButton>
                  <TwitterShareButton url={url} title={shareTitle}>
                    <ShareOption>Share on Twitter</ShareOption>
                  </TwitterShareButton>
                  <LinkedinShareButton url={url} title={shareTitle} summary={shareSummary} source={url}>
                    <ShareOption>Share on LinkedIn</ShareOption>
                  </LinkedinShareButton>
                  <ShareOption onClick={() => handleShare('link')}>Copy Link</ShareOption></>)}
                  <ShareOption onClick={() => handleShare('image')}>Download Image</ShareOption>
                </ShareMenu>
            )}
          </ShareButton>
        </HeaderRight>
      </Header>
  );
};

export default ColoringHeader;
